import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  DatePicker,
  Select,
  Button,
  Table,
  Tag,
  Input,
  Collapse,
  Space,
  Drawer,
  Form,
  Pagination,
  message,
  Spin,
  Modal,
  Tabs,
} from 'antd';
import {
  FileTextOutlined,
  UserOutlined,
  ShopOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useQuery, useQueryClient } from 'react-query';
import api from '../config/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'];

const Analytics: React.FC = () => {
  console.log('[Analytics] Component rendering...');
  const navigate = useNavigate();
  
  const [dateRange, setDateRange] = useState<any>(null);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [priority, setPriority] = useState<string | undefined>(undefined);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [businessTicketsModalVisible, setBusinessTicketsModalVisible] = useState(false);
  const [daysRange, setDaysRange] = useState<number>(7);
  const [quickDateFilter, setQuickDateFilter] = useState<string | null>(null);
  const [topBusinessesPage, setTopBusinessesPage] = useState<number>(1);
  const [topBusinessesPageSize] = useState<number>(10);
  const [selectedCustomFields, setSelectedCustomFields] = useState<string[]>([]);
  const [ticketsModalVisible, setTicketsModalVisible] = useState(false);
  const [selectedFieldForTickets, setSelectedFieldForTickets] = useState<{ fieldKey: string; fieldLabel: string; value: string } | null>(null);
  const [chronicSignatureField, setChronicSignatureField] = useState<string | undefined>(undefined);
  const [chronicTicketsModalVisible, setChronicTicketsModalVisible] = useState(false);
  const [chronicTicketRow, setChronicTicketRow] = useState<{
    business_id: string;
    category_id: string | null;
    issue_value: string;
  } | null>(null);
  const queryClient = useQueryClient();

  // Handle quick date filter changes
  const handleQuickDateFilter = (days: number | null) => {
    if (days === null) {
      setQuickDateFilter(null);
      setDateRange(null);
      setDaysRange(7); // Reset to default
      return;
    }
    setQuickDateFilter(days.toString());
    setDaysRange(days); // Update daysRange to match the filter
    const endDate = dayjs();
    const startDate = endDate.subtract(days - 1, 'day');
    setDateRange([startDate, endDate]);
  };

  // Build params for API calls
  const getParams = () => {
    const params: any = {};
    if (dateRange && dateRange.length === 2) {
      params.from = dateRange[0].startOf('day').toISOString();
      params.to = dateRange[1].endOf('day').toISOString();
    }
    if (status) params.status = status;
    if (priority) params.priority = priority;
    return params;
  };

  // Fetch summary stats (no filters - always shows all data)
  const { data: summaryData, isLoading: summaryLoading, error: summaryError } = useQuery(
    ['analytics-summary'],
    async () => {
      console.log('[Analytics] Fetching summary data...');
      try {
        const summaryRes = await api.get('/api/analytics/summary');
        
        return {
          totals: summaryRes.data.totals,
          statusCounts: summaryRes.data.statusCounts,
        };
      } catch (error) {
        console.error('[Analytics] Error fetching summary data:', error);
        throw error;
      }
    },
    {
      retry: false,
      onError: (error) => {
        console.error('[Analytics] Summary query error:', error);
        message.error('Failed to load summary data');
      }
    }
  );

  // Fetch chart data (with filters applied)
  const { data: chartData, isLoading: chartLoading, error: chartError } = useQuery(
    ['analytics-charts', dateRange?.[0]?.toISOString(), dateRange?.[1]?.toISOString(), status, priority, daysRange], 
    async () => {
      console.log('[Analytics] Fetching chart data...');
      try {
        const params = getParams();
        const [byDayRes, slaRes, priorityRes, agentRes, typesRes, statusRes] = await Promise.all([
          api.get('/api/analytics/tickets-by-day', { params: { ...params, days: daysRange } }),
          api.get('/api/analytics/sla', { params }),
          api.get('/api/analytics/priority-distribution', { params }),
          api.get('/api/analytics/agent-performance', { params }),
          api.get('/api/analytics/ticket-categories', { params }),
          api.get('/api/analytics/summary', { params }),
        ]);
        
        console.log('[Analytics] Chart data loaded successfully');
        return {
          byDay: byDayRes.data.days,
          sla: slaRes.data,
          priorities: priorityRes.data.priorities,
          agents: agentRes.data.agents,
          ticketCategories: typesRes.data.categories,
          statusCounts: statusRes.data.statusCounts,
        };
      } catch (error) {
        console.error('[Analytics] Error fetching chart data:', error);
        throw error;
      }
    },
    {
      retry: false,
      onError: (error) => {
        console.error('[Analytics] Chart query error:', error);
        message.error('Failed to load chart data');
      }
    }
  );

  // Fetch top 25 businesses
  const { data: topBusinessesData, isLoading: topBusinessesLoading } = useQuery(
    ['top-businesses-25'],
    async () => {
      const response = await api.get('/api/analytics/top-businesses', {
        params: { limit: 25 }
      });
      return response.data;
    },
    {
      retry: false,
      onError: (error) => {
        console.error('[Analytics] Top businesses query error:', error);
        message.error('Failed to load top businesses');
      }
    }
  );

  const { data: topIssuesData, isLoading: topIssuesLoading, refetch: refetchTopIssues } = useQuery(
    ['analytics-top-issues', dateRange?.[0]?.toISOString(), dateRange?.[1]?.toISOString(), status, priority],
    async () => {
      const params = {
        ...getParams(),
        limit: 10,
      };
      const response = await api.get('/api/analytics/top-issues', { params });
      return response.data;
    },
    {
      retry: false,
      onError: (error) => {
        console.error('[Analytics] Top issues query error:', error);
        message.error('Failed to load top issues');
      }
    }
  );

  // Fetch tickets for selected custom field value
  const { data: filteredTicketsData, isLoading: filteredTicketsLoading } = useQuery(
    ['analytics-filtered-tickets', selectedFieldForTickets?.fieldKey, selectedFieldForTickets?.value, dateRange?.[0]?.toISOString(), dateRange?.[1]?.toISOString(), status, priority],
    async () => {
      if (!selectedFieldForTickets) return null;
      const params = {
        custom_field_key: selectedFieldForTickets.fieldKey,
        custom_field_value: selectedFieldForTickets.value,
        ...getParams(),
        limit: 1000, // Get all matching tickets for export
      };
      const response = await api.get('/api/analytics/tickets-by-custom-field', { params });
      return response.data;
    },
    {
      enabled: !!selectedFieldForTickets,
      retry: false,
      onError: (error) => {
        console.error('[Analytics] Filtered tickets query error:', error);
        message.error('Failed to load tickets');
      }
    }
  );

  const handleCountClick = (fieldKey: string, fieldLabel: string, value: string) => {
    setSelectedFieldForTickets({ fieldKey, fieldLabel, value });
    setTicketsModalVisible(true);
  };

  const handleExportToExcel = () => {
    if (!filteredTicketsData?.tickets || filteredTicketsData.tickets.length === 0) {
      message.warning('No tickets to export');
      return;
    }

    const tickets = filteredTicketsData.tickets;
    const worksheetData = tickets.map((ticket: any) => ({
      'Ticket #': ticket.ticket_number,
      'Subject': ticket.subject,
      'Business': ticket.business_name || '',
      'Status': ticket.status,
      'Priority': ticket.priority,
      'Assignee': ticket.assignee_first_name && ticket.assignee_last_name 
        ? `${ticket.assignee_first_name} ${ticket.assignee_last_name}` 
        : 'Unassigned',
      'Category': ticket.category_name || '',
      'Created': dayjs(ticket.created_at).format('YYYY-MM-DD HH:mm'),
      'Updated': dayjs(ticket.updated_at).format('YYYY-MM-DD HH:mm'),
      'Resolved': ticket.resolved_at ? dayjs(ticket.resolved_at).format('YYYY-MM-DD HH:mm') : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tickets');

    const fileName = `${selectedFieldForTickets?.fieldLabel || 'Tickets'}_${selectedFieldForTickets?.value || 'export'}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    message.success(`Exported ${tickets.length} tickets to ${fileName}`);
  };

  // Fetch tickets for selected business (must be before early returns)
  const { data: businessTicketsData, isLoading: businessTicketsLoading } = useQuery(
    ['business-tickets', selectedBusiness?.id],
    async () => {
      if (!selectedBusiness?.id) return null;
      const response = await api.get('/api/analytics/tickets', {
        params: { business_id: selectedBusiness.id, limit: 100 }
      });
      return response.data;
    },
    { enabled: !!selectedBusiness?.id }
  );

  // Get top issues fields data (before early returns for useEffect)
  const topIssuesFields = topIssuesData?.fields || [];

  const { data: signatureConfig } = useQuery(
    ['issue-signature-config'],
    async () => (await api.get('/api/analytics/issue-signature-config')).data,
    { retry: false }
  );

  const chronicParams = () => {
    const params: Record<string, string | number> = {
      ...getParams(),
      repeat_days: 14,
      min_tickets: 3,
      limit: 50,
    };
    if (chronicSignatureField) params.signature_field_key = chronicSignatureField;
    return params;
  };

  const { data: chronicData, isLoading: chronicLoading, refetch: refetchChronic } = useQuery(
    [
      'analytics-chronic-issues',
      dateRange?.[0]?.toISOString(),
      dateRange?.[1]?.toISOString(),
      status,
      priority,
      chronicSignatureField,
    ],
    async () => (await api.get('/api/analytics/chronic-issues', { params: chronicParams() })).data,
    { retry: false }
  );

  const { data: businessStressData, isLoading: businessStressLoading } = useQuery(
    [
      'analytics-business-stress',
      dateRange?.[0]?.toISOString(),
      dateRange?.[1]?.toISOString(),
      status,
      priority,
      chronicSignatureField,
    ],
    async () => {
      const params: Record<string, string | number> = { ...getParams(), repeat_days: 14, min_tickets: 3, limit: 25 };
      if (chronicSignatureField) params.signature_field_key = chronicSignatureField;
      return (await api.get('/api/analytics/business-stress', { params })).data;
    },
    { retry: false }
  );

  const { data: reopenSummaryData, isLoading: reopenLoading } = useQuery(
    ['analytics-reopen-summary', dateRange?.[0]?.toISOString(), dateRange?.[1]?.toISOString()],
    async () => {
      const params = getParams();
      return (await api.get('/api/analytics/reopen-summary', { params })).data;
    },
    { retry: false }
  );

  const { data: chronicTicketsModalData, isLoading: chronicTicketsModalLoading } = useQuery(
    [
      'analytics-chronic-issues-tickets',
      chronicTicketRow?.business_id,
      chronicTicketRow?.category_id,
      chronicTicketRow?.issue_value,
      chronicSignatureField,
      dateRange?.[0]?.toISOString(),
      dateRange?.[1]?.toISOString(),
    ],
    async () => {
      if (!chronicTicketRow) return null;
      const params: Record<string, string | number | undefined> = {
        ...getParams(),
        business_id: chronicTicketRow.business_id,
        category_id:
          chronicTicketRow.category_id === null || chronicTicketRow.category_id === undefined
            ? 'null'
            : chronicTicketRow.category_id,
        issue_value: chronicTicketRow.issue_value,
        limit: 200,
      };
      if (chronicSignatureField) params.signature_field_key = chronicSignatureField;
      return (await api.get('/api/analytics/chronic-issues-tickets', { params })).data;
    },
    { enabled: !!chronicTicketRow && chronicTicketsModalVisible, retry: false }
  );

  // Auto-select all fields when data loads (must be before early returns)
  useEffect(() => {
    if (topIssuesFields.length > 0 && selectedCustomFields.length === 0) {
      setSelectedCustomFields(topIssuesFields.map((f: any) => f.field_key));
    }
  }, [topIssuesFields, selectedCustomFields.length]);

  // Filter fields based on selection
  const filteredFields = topIssuesFields.filter((field: any) => 
    selectedCustomFields.length === 0 || selectedCustomFields.includes(field.field_key)
  );

  // Show loading only for initial load (when both are loading)
  if (summaryLoading && chartLoading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Loading analytics..." />
      </div>
    );
  }

  // Show error only if summary fails (charts can fail independently)
  if (summaryError) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Title level={4} type="danger">Failed to Load Analytics</Title>
          <Text>An error occurred while loading analytics data. Please try refreshing the page.</Text>
        </Card>
      </div>
    );
  }

  // Use real data or fallback to empty
  const summary = summaryData || {
    totals: { tickets: 0, businesses: 0, users: 0 },
    statusCounts: { new: 0, open: 0, pending: 0, solved: 0 },
  };

  const charts = chartData || {
    byDay: [],
    sla: { withinSLA: 0, outsideSLA: 0 },
    priorities: [],
    agents: [],
    ticketCategories: [],
    statusCounts: { new: 0, open: 0, pending: 0, solved: 0 },
  };

  const handleBusinessClick = (business: any) => {
    setSelectedBusiness(business);
    setBusinessTicketsModalVisible(true);
  };

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <Title level={2} style={{ marginBottom: 24 }}>Analytics & Reports</Title>
      
      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Tickets"
              value={summary.totals.tickets}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Businesses"
              value={summary.totals.businesses}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Users"
              value={summary.totals.users}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Solved"
              value={summary.statusCounts.solved}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick date range applies to charts and Chronic & stress */}
      <Card style={{ marginBottom: 24 }}>
        <Space size="middle" wrap>
          <Text strong>Quick Date Filters:</Text>
          <Button
            type={quickDateFilter === '7' ? 'primary' : 'default'}
            size="small"
            onClick={() => handleQuickDateFilter(7)}
          >
            7 Days
          </Button>
          <Button
            type={quickDateFilter === '30' ? 'primary' : 'default'}
            size="small"
            onClick={() => handleQuickDateFilter(30)}
          >
            30 Days
          </Button>
          <Button
            type={quickDateFilter === '90' ? 'primary' : 'default'}
            size="small"
            onClick={() => handleQuickDateFilter(90)}
          >
            90 Days
          </Button>
          <Button
            type={quickDateFilter === null && dateRange === null ? 'primary' : 'default'}
            size="small"
            onClick={() => handleQuickDateFilter(null)}
          >
            All Time
          </Button>
          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setQuickDateFilter(null);
                setDateRange(dates);
                const daysDiff = dates[1].diff(dates[0], 'day') + 1;
                setDaysRange(Math.min(daysDiff, 90));
              } else {
                setDateRange(null);
                setDaysRange(7);
              }
            }}
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
            allowClear
          />
          {dateRange && (
            <Button
              size="small"
              onClick={() => {
                setDateRange(null);
                setQuickDateFilter(null);
                setDaysRange(7);
              }}
            >
              Clear Date Filter
            </Button>
          )}
        </Space>
      </Card>

      <Tabs
        defaultActiveKey="overview"
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <>
      {/* Top 25 Businesses with Pagination */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ShopOutlined />
                <span>Top 25 Businesses - Most Tickets Generated</span>
              </div>
            }
          >
            {topBusinessesLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : topBusinessesData?.businesses && topBusinessesData.businesses.length > 0 ? (
              <div>
                {(() => {
                  const startIndex = (topBusinessesPage - 1) * topBusinessesPageSize;
                  const endIndex = startIndex + topBusinessesPageSize;
                  const paginatedBusinesses = topBusinessesData.businesses.slice(startIndex, endIndex);
                  const totalPages = Math.ceil(topBusinessesData.businesses.length / topBusinessesPageSize);
                  
                  return (
                    <>
                      {paginatedBusinesses.map((business: any, index: number) => {
                        const globalIndex = startIndex + index;
                        // Build address from available parts
                        const addressParts = [
                          business.address,
                          business.city,
                          business.state,
                          business.zip_code
                        ].filter((part: any) => part && String(part).trim() !== '');
                        const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : null;
                        
                        return (
                          <div
                            key={business.id || globalIndex}
                            onClick={() => handleBusinessClick(business)}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '16px',
                              marginBottom: index < paginatedBusinesses.length - 1 ? '12px' : 0,
                              background: globalIndex === 0 ? '#f0f9ff' : '#fafafa',
                              border: globalIndex === 0 ? '2px solid #1890ff' : '1px solid #e8e8e8',
                              borderRadius: '4px',
                              transition: 'all 0.3s',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = globalIndex === 0 ? '#e6f7ff' : '#f0f0f0';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = globalIndex === 0 ? '#f0f9ff' : '#fafafa';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    background: globalIndex === 0 ? '#1890ff' : globalIndex === 1 ? '#52c41a' : globalIndex === 2 ? '#faad14' : '#d9d9d9',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '13px',
                                  }}
                                >
                                  {globalIndex + 1}
                                </span>
                                <Text strong style={{ fontSize: '16px' }}>
                                  {business.business_name || 'Unknown Business'}
                                </Text>
                              </div>
                              <div style={{ marginLeft: '36px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {business.mid && (
                                  <Text type="secondary" style={{ fontSize: '13px' }}>
                                    <strong>MID:</strong> {business.mid}
                                  </Text>
                                )}
                                {fullAddress && (
                                  <Text type="secondary" style={{ fontSize: '13px' }}>
                                    <strong>Address:</strong> {fullAddress}
                                  </Text>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                              <div style={{ textAlign: 'right' }}>
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                  Total Tickets
                                </Text>
                                <Text strong style={{ fontSize: '20px', color: '#1890ff' }}>
                                  {business.ticket_count || 0}
                                </Text>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                          current={topBusinessesPage}
                          total={topBusinessesData.businesses.length}
                          pageSize={topBusinessesPageSize}
                          showSizeChanger={false}
                          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} businesses`}
                          onChange={(page) => setTopBusinessesPage(page)}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No business data available</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Top Issues (Custom Fields) */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileTextOutlined />
                <span>Top Issues - Custom Fields</span>
              </div>
            }
            extra={
              <Space>
                {topIssuesFields.length > 0 && (
                  <Select
                    mode="multiple"
                    placeholder="Select custom fields to display"
                    value={selectedCustomFields}
                    onChange={setSelectedCustomFields}
                    style={{ minWidth: 300 }}
                    maxTagCount="responsive"
                    allowClear
                  >
                    {topIssuesFields.map((field: any) => (
                      <Select.Option key={field.field_key} value={field.field_key}>
                        {field.label} ({field.usage_count} tickets)
                      </Select.Option>
                    ))}
                  </Select>
                )}
                <Button size="small" icon={<ReloadOutlined />} onClick={() => refetchTopIssues()}>
                  Refresh
                </Button>
              </Space>
            }
          >
            {topIssuesLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : !topIssuesFields || topIssuesFields.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No custom fields found. Add ticket custom fields to analyze top issues.</Text>
              </div>
            ) : filteredFields.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No custom fields selected. Please select fields from the dropdown above.</Text>
              </div>
            ) : (
              <div>
                {filteredFields.map((field: any, fieldIndex: number) => (
                  <div key={field.field_key} style={{ marginBottom: fieldIndex < filteredFields.length - 1 ? 32 : 0 }}>
                    <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                      <Text strong style={{ fontSize: '16px' }}>
                        {field.label}
                      </Text>
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: '13px' }}>
                        ({field.usage_count} tickets)
                      </Text>
                    </div>
                    {field.issues && field.issues.length > 0 ? (
                      <Table
                        dataSource={field.issues.map((issue: any, index: number) => ({
                          key: `${field.field_key}-${issue.value}-${index}`,
                          issue: issue.value || 'Unknown',
                          count: issue.count || 0,
                        }))}
                        columns={[
                          {
                            title: 'Issue',
                            dataIndex: 'issue',
                            key: 'issue',
                            render: (text: string, record: any, index: number) => (
                              <Space>
                                <Tag color={index === 0 ? 'blue' : index === 1 ? 'green' : 'geekblue'}>{index + 1}</Tag>
                                <span>{text}</span>
                              </Space>
                            ),
                          },
                          {
                            title: 'Ticket Count',
                            dataIndex: 'count',
                            key: 'count',
                            width: 150,
                            render: (count: number, record: any) => (
                              <Tag 
                                color="purple" 
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleCountClick(field.field_key, field.label, record.issue)}
                              >
                                {count}
                              </Tag>
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                        style={{ marginBottom: 0 }}
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                        <Text type="secondary">No issues found for this field.</Text>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card 
            title="Ticket reopens (audit trail)"
            extra={reopenLoading ? <Spin size="small" /> : null}
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {reopenSummaryData?.description || 'Status changes from solved back to open/pending/new, from ticket audit logs.'}
            </Text>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Total reopens in range"
                  value={reopenSummaryData?.totalReopens ?? 0}
                />
              </Col>
              <Col xs={24} sm={16}>
                {reopenLoading ? (
                  <Spin />
                ) : (
                  <Table
                    size="small"
                    dataSource={(reopenSummaryData?.byCategory || []).map((r: any, i: number) => ({ ...r, key: i }))}
                    columns={[
                      { title: 'Category', dataIndex: 'category_name', key: 'category_name' },
                      { title: 'Reopens', dataIndex: 'reopen_count', key: 'reopen_count', width: 100 },
                    ]}
                    pagination={false}
                  />
                )}
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 1 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Card 
            title="Weekly Ticket Trend" 
            style={{ marginBottom: 16 }}
            extra={chartLoading ? <Spin size="small" /> : null}
          >
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin tip="Loading chart data..." />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart 
                  data={(charts.byDay || []).map((d: any) => ({
                    day: dayjs(d.day).format('MMM D'),
                    created: d.created || 0,
                    solved: d.solved || 0,
                  }))}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="day" 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <YAxis 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e8e8e8',
                      borderRadius: '4px'
                    }}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="created" 
                    stroke="#1890ff" 
                    strokeWidth={2}
                    name="Created" 
                    dot={{ fill: '#1890ff', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="solved" 
                    stroke="#52c41a" 
                    strokeWidth={2}
                    name="Solved" 
                    dot={{ fill: '#52c41a', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Charts Row 2 - Priority Distribution & Agent Performance */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Priority Distribution" 
            style={{ marginBottom: 16 }}
            extra={chartLoading ? <Spin size="small" /> : null}
          >
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin tip="Loading chart data..." />
              </div>
            ) : (charts.priorities && charts.priorities.length > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(charts.priorities || []).map((p: any) => ({
                    name: p.priority ? p.priority.charAt(0).toUpperCase() + p.priority.slice(1) : 'Unknown',
                    value: p.count || 0,
                    color: p.priority === 'high' ? '#fa8c16' : 
                           p.priority === 'medium' ? '#faad14' : '#52c41a'
                  }))}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#666" tick={{ fill: '#666', fontSize: 12 }} />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                    width={80}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e8e8e8',
                      borderRadius: '4px'
                    }}
                    formatter={(value: any) => [value, 'Tickets']}
                  />
                  <Bar 
                  dataKey="value" 
                  radius={[0, 8, 8, 0]}
                >
                  {(charts.priorities || []).map((p: any, index: number) => {
                      const color = 
                                   p.priority === 'high' ? '#fa8c16' : 
                                   p.priority === 'medium' ? '#faad14' : '#52c41a';
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No priority data available</Text>
              </div>
            )}
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card 
            title="Top Agents by Ticket Volume" 
            style={{ marginBottom: 16 }}
            extra={chartLoading ? <Spin size="small" /> : null}
          >
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin tip="Loading chart data..." />
              </div>
            ) : (charts.agents && charts.agents.length > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(charts.agents || []).slice(0, 5).map((a: any) => ({
                    name: `${a.first_name || ''} ${a.last_name || ''}`.trim() || 'Unknown Agent',
                    total: a.total_tickets || 0,
                    solved: a.solved_tickets || 0,
                  }))}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 11 }}
                  />
                  <YAxis 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e8e8e8',
                      borderRadius: '4px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="total" fill="#1890ff" name="Total Tickets" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="solved" fill="#52c41a" name="Solved" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No agent data available</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Charts Row 3 - Ticket Types & SLA Metrics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card 
            title="Tickets by Category" 
            style={{ marginBottom: 16 }}
            extra={chartLoading ? <Spin size="small" /> : null}
          >
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin tip="Loading chart data..." />
              </div>
            ) : (charts.ticketCategories && charts.ticketCategories.length > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(charts.ticketCategories || []).map((c: any) => ({
                    name: c.category_name || 'Unassigned',
                    value: c.count || 0,
                  }))}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    stroke="#666"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e8e8e8',
                      borderRadius: '4px'
                    }}
                    formatter={(value: any) => [value, 'Tickets']}
                  />
                  <Bar 
                    dataKey="value" 
                    fill="#722ed1"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No category data available</Text>
              </div>
            )}
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card 
            title="SLA Performance" 
            style={{ marginBottom: 16 }}
            extra={chartLoading ? <Spin size="small" /> : null}
          >
            {chartLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin tip="Loading chart data..." />
              </div>
            ) : charts.sla ? (
              <div style={{ padding: '20px 0' }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="Avg Resolution Time"
                      value={charts.sla.resolve?.avg_minutes || 0}
                      suffix="min"
                      valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Avg First Response"
                      value={charts.sla.first_response?.avg_minutes || 0}
                      suffix="min"
                      valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Median Resolution"
                      value={charts.sla.resolve?.p50_minutes || 0}
                      suffix="min"
                      valueStyle={{ color: '#faad14', fontSize: '20px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="90th Percentile"
                      value={charts.sla.resolve?.p90_minutes || 0}
                      suffix="min"
                      valueStyle={{ color: '#f5222d', fontSize: '20px' }}
                    />
                  </Col>
                </Row>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text type="secondary">No SLA data available</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
              </>
            ),
          },
          {
            key: 'chronic',
            label: 'Chronic & stress',
            children: (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} lg={15}>
                    <Card
                      title={
                        <Space>
                          <AlertOutlined />
                          <span>Chronic issues (per business)</span>
                        </Space>
                      }
                      extra={
                        <Space>
                          {signatureConfig?.fields?.length ? (
                            <Select
                              allowClear
                              placeholder="Signature field"
                              style={{ minWidth: 200 }}
                              value={chronicSignatureField}
                              onChange={(v) => setChronicSignatureField(v || undefined)}
                              options={signatureConfig.fields.map((f: { field_key: string; label: string }) => ({
                                label: `${f.label} (${f.field_key})`,
                                value: f.field_key,
                              }))}
                            />
                          ) : null}
                          <Button size="small" icon={<ReloadOutlined />} onClick={() => refetchChronic()}>
                            Refresh
                          </Button>
                        </Space>
                      }
                    >
                      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        Groups tickets by business, category, and the selected custom field value (defaults to{' '}
                        <Text code>{signatureConfig?.resolvedField?.field_key || 'problem_type'}</Text> when unset).
                        Uses the quick date range above. High &quot;repeat after resolve&quot; suggests the same
                        symptom returned soon after a prior solve. Score is normalized to 1–10 within the current
                        result set (higher = more chronic pressure).
                      </Text>
                      {chronicData?.signatureField && (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                          Active signature field: <Text strong>{chronicData.signatureField.label}</Text>
                        </Text>
                      )}
                      {chronicData?.signatureFieldNote && (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                          {chronicData.signatureFieldNote}
                        </Text>
                      )}
                      {chronicLoading ? (
                        <div style={{ textAlign: 'center', padding: 32 }}>
                          <Spin size="large" />
                        </div>
                      ) : !chronicData?.rows?.length ? (
                        <Text type="secondary">
                          {chronicData?.message ||
                            'No chronic patterns match the current filters (try a longer window or lower minimum tickets on the API).'}
                        </Text>
                      ) : (
                        <Table
                          size="small"
                          scroll={{ x: true }}
                          dataSource={chronicData.rows.map((r: any, i: number) => ({ ...r, key: i }))}
                          columns={[
                            { title: 'Business', dataIndex: 'business_name', key: 'business_name', ellipsis: true },
                            {
                              title: 'Category',
                              dataIndex: 'category_name',
                              key: 'category_name',
                              render: (v: string) => v || '—',
                            },
                            {
                              title: 'Issue',
                              key: 'issue_display',
                              width: 300,
                              render: (_: unknown, row: any) => (
                                <div style={{ maxWidth: 320 }}>
                                  {row.issue_value === '(unspecified)' ? (
                                    <Tag color="default">No problem type set</Tag>
                                  ) : (
                                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.issue_value}</span>
                                  )}
                                  {row.issue_subjects_preview ? (
                                    <div style={{ marginTop: 6 }}>
                                      <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'normal' }}>
                                        <Text strong style={{ fontSize: 11 }}>Ticket subjects: </Text>
                                        {row.issue_subjects_preview}
                                      </Text>
                                    </div>
                                  ) : row.issue_value === '(unspecified)' ? (
                                    <div style={{ marginTop: 4 }}>
                                      <Text type="secondary" style={{ fontSize: 11 }}>
                                        Add problem type on tickets for clearer grouping, or open Tickets to see subjects.
                                      </Text>
                                    </div>
                                  ) : null}
                                </div>
                              ),
                            },
                            { title: 'Tickets', dataIndex: 'ticket_count', key: 'ticket_count', width: 80 },
                            { title: 'Not solved', dataIndex: 'not_solved_count', key: 'not_solved_count', width: 90 },
                            {
                              title: 'Repeat after solve',
                              dataIndex: 'repeat_after_resolve_count',
                              key: 'repeat_after_resolve_count',
                              width: 130,
                            },
                            {
                              title: 'Score (1–10)',
                              dataIndex: 'chronic_score',
                              key: 'chronic_score',
                              width: 100,
                              render: (v: number) =>
                                v != null ? Math.round(Number(v)) : '—',
                            },
                            {
                              title: '',
                              key: 'actions',
                              width: 100,
                              render: (_: unknown, row: any) => (
                                <Button
                                  type="link"
                                  size="small"
                                  onClick={() => {
                                    setChronicTicketRow({
                                      business_id: row.business_id,
                                      category_id: row.category_id,
                                      issue_value: row.issue_value,
                                    });
                                    setChronicTicketsModalVisible(true);
                                  }}
                                >
                                  Tickets
                                </Button>
                              ),
                            },
                          ]}
                          pagination={{ pageSize: 10 }}
                        />
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} lg={9}>
                    <Card title="Business stress" extra={businessStressLoading ? <Spin size="small" /> : null}>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        Stress is the sum of raw signature scores per business, then normalized to 1–10 across
                        businesses in this window (same date filters as above).
                      </Text>
                      {businessStressLoading ? (
                        <div style={{ textAlign: 'center', padding: 32 }}>
                          <Spin />
                        </div>
                      ) : !businessStressData?.rows?.length ? (
                        <Text type="secondary">No data.</Text>
                      ) : (
                        <Table
                          size="small"
                          dataSource={businessStressData.rows.map((r: any, i: number) => ({ ...r, key: i }))}
                          columns={[
                            { title: 'Business', dataIndex: 'business_name', key: 'business_name', ellipsis: true },
                            {
                              title: 'Stress (1–10)',
                              dataIndex: 'stress_score',
                              key: 'stress_score',
                              width: 110,
                              render: (v: number) => (v != null ? Math.round(Number(v)) : '—'),
                            },
                            {
                              title: 'Repeats',
                              dataIndex: 'repeat_after_resolve_sum',
                              key: 'repeat_after_resolve_sum',
                              width: 80,
                            },
                          ]}
                          pagination={false}
                        />
                      )}
                    </Card>
                  </Col>
                </Row>
              </>
            ),
          },
        ]}
      />

      {/* Business Tickets Modal */}
      <Modal
        title={
          selectedBusiness ? (
            <div>
              <Text strong style={{ fontSize: '18px' }}>Tickets for {selectedBusiness.business_name}</Text>
              {selectedBusiness.mid && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>MID: {selectedBusiness.mid}</Text>
                </div>
              )}
            </div>
          ) : 'Business Tickets'
        }
        open={businessTicketsModalVisible}
        onCancel={() => {
          setBusinessTicketsModalVisible(false);
          setSelectedBusiness(null);
        }}
        footer={null}
        width={1200}
      >
        {businessTicketsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : businessTicketsData?.tickets && businessTicketsData.tickets.length > 0 ? (
          <Table
            dataSource={businessTicketsData.tickets}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} tickets`,
            }}
            columns={[
              {
                title: 'Ticket #',
                dataIndex: 'ticket_number',
                key: 'ticket_number',
                width: 120,
                render: (text: string, record: any) => (
                  <Text 
                    code 
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                    onClick={() => navigate(`/workspace/number/${record.ticket_number}`)}
                  >
                    {text}
                  </Text>
                ),
              },
              {
                title: 'Subject',
                dataIndex: 'subject',
                key: 'subject',
                ellipsis: true,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (status: string) => {
                  const colors: any = { new: 'blue', open: 'orange', pending: 'gold', solved: 'green' };
                  return <Tag color={colors[status]}>{status?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Priority',
                dataIndex: 'priority',
                key: 'priority',
                width: 100,
                render: (priority: string) => {
                  const colors: any = { low: 'default', medium: 'blue', high: 'orange' };
                  return <Tag color={colors[priority]}>{priority?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Assignee',
                key: 'assignee',
                width: 150,
                render: (record: any) => 
                  record.assignee_first_name ? `${record.assignee_first_name} ${record.assignee_last_name}` : 'Unassigned',
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                key: 'created_at',
                width: 150,
                render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm'),
              },
              {
                title: 'Closed',
                dataIndex: 'resolved_at',
                key: 'resolved_at',
                width: 150,
                render: (date: string | null) => {
                  if (!date) {
                    return <Text type="secondary">-</Text>;
                  }
                  return dayjs(date).format('MMM D, YYYY HH:mm');
                },
              },
            ]}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">No tickets found for this business</Text>
          </div>
        )}
      </Modal>

      <Modal
        title="Chronic pattern — tickets"
        open={chronicTicketsModalVisible}
        onCancel={() => {
          setChronicTicketsModalVisible(false);
          setChronicTicketRow(null);
        }}
        footer={
          <Button
            onClick={() => {
              setChronicTicketsModalVisible(false);
              setChronicTicketRow(null);
            }}
          >
            Close
          </Button>
        }
        width={1000}
      >
        {chronicTicketsModalLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : chronicTicketsModalData?.tickets?.length ? (
          <Table
            size="small"
            rowKey="id"
            dataSource={chronicTicketsModalData.tickets}
            pagination={{ pageSize: 15 }}
            columns={[
              {
                title: 'Ticket #',
                dataIndex: 'ticket_number',
                key: 'ticket_number',
                width: 110,
                render: (text: string, record: any) => (
                  <Text
                    code
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                    onClick={() => navigate(`/workspace/number/${record.ticket_number}`)}
                  >
                    {text}
                  </Text>
                ),
              },
              { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
              { title: 'Business', dataIndex: 'business_name', key: 'business_name', ellipsis: true },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 90,
                render: (s: string) => {
                  const colors: Record<string, string> = { new: 'blue', open: 'orange', pending: 'gold', solved: 'green' };
                  return <Tag color={colors[s]}>{s?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                key: 'created_at',
                width: 150,
                render: (d: string) => dayjs(d).format('MMM D, YYYY HH:mm'),
              },
            ]}
          />
        ) : (
          <Text type="secondary">No tickets found for this pattern.</Text>
        )}
      </Modal>

      {/* Filtered Tickets Modal */}
      <Modal
        title={
          selectedFieldForTickets ? (
            <div>
              <Text strong style={{ fontSize: '18px' }}>
                Tickets: {selectedFieldForTickets.fieldLabel} = "{selectedFieldForTickets.value}"
              </Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {filteredTicketsData?.total || 0} ticket(s) found
                </Text>
              </div>
            </div>
          ) : 'Filtered Tickets'
        }
        open={ticketsModalVisible}
        onCancel={() => {
          setTicketsModalVisible(false);
          setSelectedFieldForTickets(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setTicketsModalVisible(false);
            setSelectedFieldForTickets(null);
          }}>
            Close
          </Button>,
          <Button 
            key="export" 
            type="primary" 
            icon={<FileExcelOutlined />}
            onClick={handleExportToExcel}
            disabled={!filteredTicketsData?.tickets || filteredTicketsData.tickets.length === 0}
          >
            Export to Excel
          </Button>,
        ]}
        width={1200}
      >
        {filteredTicketsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : filteredTicketsData?.tickets && filteredTicketsData.tickets.length > 0 ? (
          <Table
            dataSource={filteredTicketsData.tickets}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} tickets`,
            }}
            columns={[
              {
                title: 'Ticket #',
                dataIndex: 'ticket_number',
                key: 'ticket_number',
                width: 120,
                render: (text: string, record: any) => (
                  <Text 
                    code 
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                    onClick={() => navigate(`/workspace/number/${record.ticket_number}`)}
                  >
                    {text}
                  </Text>
                ),
              },
              {
                title: 'Subject',
                dataIndex: 'subject',
                key: 'subject',
                ellipsis: true,
              },
              {
                title: 'Business',
                dataIndex: 'business_name',
                key: 'business_name',
                ellipsis: true,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (status: string) => {
                  const colors: any = { new: 'blue', open: 'orange', pending: 'gold', solved: 'green' };
                  return <Tag color={colors[status]}>{status?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Priority',
                dataIndex: 'priority',
                key: 'priority',
                width: 100,
                render: (priority: string) => {
                  const colors: any = { low: 'default', medium: 'blue', high: 'orange' };
                  return <Tag color={colors[priority]}>{priority?.toUpperCase()}</Tag>;
                },
              },
              {
                title: 'Assignee',
                key: 'assignee',
                width: 150,
                render: (record: any) => 
                  record.assignee_first_name ? `${record.assignee_first_name} ${record.assignee_last_name}` : 'Unassigned',
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                key: 'created_at',
                width: 150,
                render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm'),
              },
              {
                title: 'Closed',
                dataIndex: 'resolved_at',
                key: 'resolved_at',
                width: 150,
                render: (date: string | null) => {
                  if (!date) {
                    return <Text type="secondary">-</Text>;
                  }
                  return dayjs(date).format('MMM D, YYYY HH:mm');
                },
              },
            ]}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">No tickets found for this filter</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

const AnalyticsOLD: React.FC = () => {
  console.log('[Analytics] Component mounted/rendered');
  
  const [dateRange, setDateRange] = useState<any>(null);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [priority, setPriority] = useState<string | undefined>(undefined);
  
  // Advanced search state
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<any>({});
  const [ticketPage, setTicketPage] = useState(1);
  const [customFieldFilters, setCustomFieldFilters] = useState<any>({});
  
  const [form] = Form.useForm();

  // Build params for API calls
  const getParams = () => {
    const params: any = {};
    if (dateRange && dateRange.length === 2) {
      params.from = dateRange[0].startOf('day').toISOString();
      params.to = dateRange[1].endOf('day').toISOString();
    }
    if (status) params.status = status;
    if (priority) params.priority = priority;
    return params;
  };

  // Fetch analytics data
  const { data: analytics, isLoading: analyticsLoading, error: analyticsError, refetch: refetchAnalytics } = useQuery(
    ['analytics', dateRange?.[0]?.toString(), dateRange?.[1]?.toString(), status, priority], 
    async () => {
      console.log('[Analytics] Starting to fetch analytics data...');
      console.log('[Analytics] Params:', getParams());
      
      try {
        const params = getParams();
        console.log('[Analytics] Making 4 parallel API calls...');
        
        const [summaryRes, byDayRes, slaRes, topBusinessesRes] = await Promise.all([
          api.get('/api/analytics/summary', { params }),
          api.get('/api/analytics/tickets-by-day', { params: { ...params, days: 7 } }),
          api.get('/api/analytics/sla', { params }),
          api.get('/api/analytics/top-businesses', { params: { ...params, limit: 10 } }),
        ]);
        
        console.log('[Analytics] All API calls completed successfully');
        console.log('[Analytics] Summary data:', summaryRes.data);
        console.log('[Analytics] By day data:', byDayRes.data);
        console.log('[Analytics] SLA data:', slaRes.data);
        console.log('[Analytics] Top businesses data:', topBusinessesRes.data);
        
        const result = {
          totals: summaryRes.data.totals,
          statusCounts: summaryRes.data.statusCounts,
          byDay: byDayRes.data.days,
          sla: slaRes.data,
          topBusinesses: topBusinessesRes.data.businesses,
        };
        
        console.log('[Analytics] Returning analytics result:', result);
        return result;
      } catch (error) {
        console.error('[Analytics] Error fetching analytics data:', error);
        throw error;
      }
    }
  );

  // Fetch custom fields - TEMPORARILY DISABLED FOR TESTING
  // const { data: customFields } = useQuery('analytics-custom-fields', async () => {
  //   console.log('[Analytics] Fetching custom fields...');
  //   try {
  //     const res = await api.get('/api/analytics/custom-fields');
  //     console.log('[Analytics] Custom fields response:', res.data);
  //     return res.data.fields || [];
  //   } catch (error) {
  //     console.error('[Analytics] Custom fields error:', error);
  //     return [];
  //   }
  // });
  const legacyCustomFields: string[] = []; // Temporarily disabled
  console.log('[Analytics] Custom fields temporarily disabled for testing');

  // Fetch users for assignee filter
  const { data: users } = useQuery('users-list', async () => {
    console.log('[Analytics] Fetching users...');
    try {
      const res = await api.get('/api/users');
      console.log('[Analytics] Users response:', res.data?.length || 0, 'users');
      return res.data || [];
    } catch (error) {
      console.error('[Analytics] Users error:', error);
      return [];
    }
  });

  // Fetch businesses for business filter
  const { data: businesses } = useQuery('businesses-list', async () => {
    console.log('[Analytics] Fetching businesses...');
    try {
      const res = await api.get('/api/businesses?limit=100');
      console.log('[Analytics] Businesses response:', res.data?.businesses?.length || 0, 'businesses');
      return res.data.businesses || [];
    } catch (error) {
      console.error('[Analytics] Businesses error:', error);
      return [];
    }
  });

  // Advanced ticket search
  const { data: ticketsData, refetch: refetchTickets, isLoading: ticketsLoading } = useQuery(
    ['analytics-tickets', advancedFilters, ticketPage],
    async () => {
      const params: any = { ...advancedFilters, page: ticketPage, limit: 50 };
      if (Object.keys(customFieldFilters).length > 0) {
        params.custom_fields = JSON.stringify(customFieldFilters);
      }
      const res = await api.get('/api/analytics/tickets', { params });
      return res.data;
    },
    { enabled: showAdvancedSearch && Object.keys(advancedFilters).length > 0 }
  );

  const handleAdvancedSearch = (values: any) => {
    const filters: any = {};
    const newCustomFieldFilters: any = {};
    
    Object.entries(values).forEach(([key, value]) => {
      if (value && key.startsWith('custom_')) {
        const fieldName = key.replace('custom_', '');
        newCustomFieldFilters[fieldName] = value;
      } else if (value) {
        if (key === 'dateRange' && Array.isArray(value) && value.length === 2) {
          filters.from = value[0].startOf('day').toISOString();
          filters.to = value[1].endOf('day').toISOString();
        } else if (key !== 'dateRange') {
          filters[key] = value;
        }
      }
    });
    
    setAdvancedFilters(filters);
    setCustomFieldFilters(newCustomFieldFilters);
    setTicketPage(1);
    message.success('Search filters applied');
  };

  const resetFilters = () => {
    form.resetFields();
    setAdvancedFilters({});
    setCustomFieldFilters({});
    setTicketPage(1);
    message.info('Filters reset');
  };

  const exportToCSV = () => {
    if (!ticketsData?.tickets || ticketsData.tickets.length === 0) {
      message.warning('No data to export');
      return;
    }

    const headers = ['Ticket #', 'Subject', 'Business', 'Status', 'Priority', 'Assignee', 'Created', 'Updated'];
    const rows = ticketsData.tickets.map((t: any) => [
      t.ticket_number,
      t.subject,
      t.business_name || '',
      t.status,
      t.priority,
      t.assignee_first_name ? `${t.assignee_first_name} ${t.assignee_last_name}` : 'Unassigned',
      dayjs(t.created_at).format('YYYY-MM-DD HH:mm'),
      dayjs(t.updated_at).format('YYYY-MM-DD HH:mm'),
    ]);

    const csvContent = [headers, ...rows]
      .map((row: any) => row.map((cell: any) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `analytics_tickets_${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
    message.success('Exported to CSV');
  };

  const businessColumns = [
    {
      title: 'Business Name',
      dataIndex: 'business_name',
      key: 'business_name',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.mid && <Text type="secondary" style={{ fontSize: 12 }}>MID: {record.mid}</Text>}
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (record: any) => (
        <div>
          {record.email && <div style={{ fontSize: 12 }}>{record.email}</div>}
          {record.phone && <Text type="secondary" style={{ fontSize: 12 }}>{record.phone}</Text>}
        </div>
      ),
    },
    {
      title: 'Total Tickets',
      dataIndex: 'ticket_count',
      key: 'ticket_count',
      align: 'center' as const,
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Solved',
      dataIndex: 'solved_count',
      key: 'solved_count',
      align: 'center' as const,
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
    {
      title: 'Open',
      dataIndex: 'open_count',
      key: 'open_count',
      align: 'center' as const,
      render: (count: number) => <Tag color="orange">{count}</Tag>,
    },
    {
      title: 'Pending',
      dataIndex: 'pending_count',
      key: 'pending_count',
      align: 'center' as const,
      render: (count: number) => <Tag color="gold">{count}</Tag>,
    },
  ];

  const ticketColumns = [
    {
      title: 'Ticket #',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      width: 120,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Business',
      dataIndex: 'business_name',
      key: 'business_name',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const colors: any = { new: 'blue', open: 'orange', pending: 'gold', solved: 'green' };
        return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: string) => {
        const colors: any = { low: 'default', medium: 'blue', high: 'orange', urgent: 'red' };
        return <Tag color={colors[priority]}>{priority?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Assignee',
      key: 'assignee',
      width: 150,
      render: (record: any) => 
        record.assignee_first_name ? `${record.assignee_first_name} ${record.assignee_last_name}` : 'Unassigned',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
  ];

  const statusData = [
    { name: 'New', value: analytics?.statusCounts?.new || 0, color: '#1890ff' },
    { name: 'Open', value: analytics?.statusCounts?.open || 0, color: '#fa8c16' },
    { name: 'Pending', value: analytics?.statusCounts?.pending || 0, color: '#faad14' },
    { name: 'Solved', value: analytics?.statusCounts?.solved || 0, color: '#52c41a' },
  ];

  // Log component state
  console.log('[Analytics] Component render state:', {
    analyticsLoading,
    hasAnalyticsData: !!analytics,
    analyticsError,
    customFields: 'disabled',
    users: users?.length || 0,
    businesses: businesses?.length || 0,
  });

  // Show loading spinner while initial data is loading
  if (analyticsLoading) {
    console.log('[Analytics] Showing loading spinner...');
    return (
      <div style={{ 
        padding: 24, 
        background: '#f0f2f5', 
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" tip="Loading analytics..." />
      </div>
    );
  }

  // Show error if analytics failed to load
  if (analyticsError) {
    console.error('[Analytics] Analytics error:', analyticsError);
    return (
      <div style={{ 
        padding: 24, 
        background: '#f0f2f5', 
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        gap: 16,
      }}>
        <Typography.Title level={4} type="danger">
          Failed to Load Analytics
        </Typography.Title>
        <Typography.Text type="secondary">
          {(analyticsError as any)?.message || 'Unknown error'}
        </Typography.Text>
        <Button type="primary" onClick={() => refetchAnalytics()}>
          Retry
        </Button>
      </div>
    );
  }

  console.log('[Analytics] Rendering main analytics dashboard...');

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>Analytics & Reports</Title>
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => refetchAnalytics()}
          >
            Refresh
          </Button>
          <Button 
            type="primary" 
            icon={<FilterOutlined />}
            onClick={() => setShowAdvancedSearch(true)}
          >
            Advanced Search
          </Button>
        </Space>
      </div>

      {/* Quick Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Text strong>Quick Filters:</Text>
          <RangePicker 
            value={dateRange}
            onChange={setDateRange}
            style={{ width: 300 }}
          />
          <Select 
            allowClear 
            style={{ width: 140 }} 
            placeholder="Status" 
            value={status} 
            onChange={setStatus}
          >
          <Select.Option value="new">New</Select.Option>
          <Select.Option value="open">Open</Select.Option>
          <Select.Option value="pending">Pending</Select.Option>
          <Select.Option value="solved">Solved</Select.Option>
              </Select>
          <Select 
            allowClear 
            style={{ width: 140 }} 
            placeholder="Priority" 
            value={priority} 
            onChange={setPriority}
          >
          <Select.Option value="low">Low</Select.Option>
          <Select.Option value="medium">Medium</Select.Option>
          <Select.Option value="high">High</Select.Option>
              </Select>
      </div>
      </Card>

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Tickets"
              value={analytics?.totals?.tickets || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Solved Tickets"
              value={analytics?.statusCounts?.solved || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={analytics?.totals?.users || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Businesses"
              value={analytics?.totals?.businesses || 0}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Row 1 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={16}>
          <Card title="Weekly Ticket Trend">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={(analytics?.byDay || []).map((d: any) => ({ 
                name: dayjs(d.day).format('MMM D'), 
                tickets: d.created, 
                solved: d.solved 
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="tickets" stroke="#1890ff" name="Created" strokeWidth={2} />
                <Line type="monotone" dataKey="solved" stroke="#52c41a" name="Solved" strokeWidth={2} />
              </LineChart>
                </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Status Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
                </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Top Businesses Report */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ShopOutlined />
                <span>Top Businesses by Ticket Volume</span>
              </div>
            }
          >
            <Table
              dataSource={analytics?.topBusinesses || []}
              columns={businessColumns}
              rowKey="id"
              pagination={false}
              size="middle"
            />
          </Card>
        </Col>
      </Row>

      {/* SLA Metrics */}
      {analytics?.sla && (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Average Time to Resolve">
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Average"
                    value={analytics.sla.resolve.avg_minutes || 0}
                    suffix="min"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Median"
                    value={analytics.sla.resolve.p50_minutes || 0}
                    suffix="min"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="90th %ile"
                    value={analytics.sla.resolve.p90_minutes || 0}
                    suffix="min"
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Average Time to First Response">
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Average"
                    value={analytics.sla.first_response.avg_minutes || 0}
                    suffix="min"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Median"
                    value={analytics.sla.first_response.p50_minutes || 0}
                    suffix="min"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="90th %ile"
                    value={analytics.sla.first_response.p90_minutes || 0}
                    suffix="min"
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Advanced Search Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SearchOutlined />
            <span>Advanced Ticket Search</span>
          </div>
        }
        placement="right"
        width={600}
        onClose={() => setShowAdvancedSearch(false)}
        open={showAdvancedSearch}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAdvancedSearch}
        >
          <Form.Item name="dateRange" label="Date Range">
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select allowClear placeholder="Any">
                  <Select.Option value="new">New</Select.Option>
                  <Select.Option value="open">Open</Select.Option>
                  <Select.Option value="pending">Pending</Select.Option>
                  <Select.Option value="solved">Solved</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label="Priority">
                <Select allowClear placeholder="Any">
                  <Select.Option value="low">Low</Select.Option>
                  <Select.Option value="medium">Medium</Select.Option>
                  <Select.Option value="high">High</Select.Option>
                  <Select.Option value="urgent">Urgent</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="business_id" label="Business">
            <Select 
              allowClear 
              showSearch
              placeholder="Select business"
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {businesses?.map((b: any) => (
                <Select.Option key={b.id} value={b.id}>
                  {b.business_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="assigned_to" label="Assigned To">
            <Select 
              allowClear 
              showSearch
              placeholder="Select user"
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {users?.map((u: any) => (
                <Select.Option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ticket_type" label="Ticket Type">
                <Select allowClear placeholder="Any">
                  <Select.Option value="question">Question</Select.Option>
                  <Select.Option value="incident">Incident</Select.Option>
                  <Select.Option value="problem">Problem</Select.Option>
                  <Select.Option value="task">Task</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="called_type" label="Called Type">
                <Select allowClear placeholder="Any">
                  <Select.Option value="inbound">Inbound</Select.Option>
                  <Select.Option value="outbound">Outbound</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="service_type" label="Service Type">
            <Input allowClear placeholder="Enter service type" />
          </Form.Item>

          {legacyCustomFields && legacyCustomFields.length > 0 && (
            <Collapse ghost>
              <Panel header="Custom Fields" key="1">
                {legacyCustomFields.map((field: string) => (
                  <Form.Item 
                    key={field} 
                    name={`custom_${field}`} 
                    label={field}
                  >
                    <Input allowClear placeholder={`Filter by ${field}`} />
                  </Form.Item>
                ))}
              </Panel>
            </Collapse>
          )}

          <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />} block>
              Search Tickets
            </Button>
            <Button onClick={resetFilters} icon={<ReloadOutlined />}>
              Reset
            </Button>
          </div>
        </Form>

        {ticketsData && ticketsData.tickets && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Text strong>Search Results: {ticketsData.total} tickets</Text>
              <Button 
                icon={<DownloadOutlined />}
                onClick={exportToCSV}
                size="small"
              >
                Export CSV
              </Button>
            </div>
            <Table
              dataSource={ticketsData.tickets}
              columns={ticketColumns}
              rowKey="id"
              pagination={false}
              loading={ticketsLoading}
              size="small"
              scroll={{ x: 800 }}
            />
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Pagination
                current={ticketPage}
                total={ticketsData.total}
                pageSize={50}
                onChange={setTicketPage}
                showSizeChanger={false}
                showTotal={(total) => `Total ${total} tickets`}
              />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}; // END OF OLD ANALYTICS

export default Analytics;
